import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { getSupabaseClient, requestStagingPasswordReset, signInWithStagingPassword } from '../offline/supabase'
import { Brand } from './Brand'

export interface OwnerAccessAdapter {
  getAuthenticated: () => Promise<boolean>
  subscribe: (onAuthenticated: (authenticated: boolean) => void) => () => void
  signIn: (email: string, password: string) => Promise<boolean>
  requestPasswordReset: (email: string) => Promise<boolean>
}

export function syntheticPreviewEnabled(): boolean {
  return import.meta.env.MODE === 'test' || import.meta.env.VITE_ENABLE_SYNTHETIC_PREVIEW === 'true'
}

function configuredOwnerAccess(): OwnerAccessAdapter | null {
  const client = getSupabaseClient()
  if (!client) return null
  return {
    async getAuthenticated() {
      const { data, error } = await client.auth.getSession()
      if (error) throw error
      return data.session !== null
    },
    subscribe(onAuthenticated) {
      const { data } = client.auth.onAuthStateChange((_event, session) => onAuthenticated(session !== null))
      return () => data.subscription.unsubscribe()
    },
    async signIn(email, password) {
      const { error } = await signInWithStagingPassword(email, password)
      return error === null
    },
    async requestPasswordReset(email) {
      const { error } = await requestStagingPasswordReset(email)
      return error === null
    },
  }
}

function OwnerSignIn({ access }: { access: OwnerAccessAdapter }) {
  const [status, setStatus] = useState('Sign in with the staging owner account to open your matrix.')
  const [busy, setBusy] = useState(false)
  const [resetBusy, setResetBusy] = useState(false)
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    setBusy(true); setStatus('Signing in…')
    try {
      const accepted = await access.signIn(String(data.get('email') ?? ''), String(data.get('password') ?? ''))
      if (!accepted) setStatus('Sign in was not accepted. Check the staging owner email and password.')
    } catch {
      setStatus('Sign in could not be completed. Check the connection and try again.')
    } finally { setBusy(false) }
  }
  const reset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    const email = String(new FormData(form).get('recovery-email') ?? '')
    setResetBusy(true); setStatus('Requesting a password setup link…')
    try {
      await access.requestPasswordReset(email)
      form.reset()
      setStatus('If that staging owner exists, a password setup link has been sent.')
    } catch {
      setStatus('The password setup request could not be sent. Wait a moment and try again.')
    } finally { setResetBusy(false) }
  }
  return <main className="centred-page"><section className="auth-card panel" aria-labelledby="owner-sign-in-title"><Brand /><span className="overline">Private owner workspace</span><h1 id="owner-sign-in-title">Sign in to Ops Matrix</h1><p>Your owner data is not loaded until authentication succeeds. Public wall links continue to work without signing in.</p><form className="staging-signin" onSubmit={(event) => void submit(event)}><label>Email<input name="email" type="email" autoComplete="username" required /></label><label>Password<input name="password" type="password" autoComplete="current-password" required /></label><button className="button button-primary" type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button></form><details className="connection-recovery"><summary>Set or reset your password</summary><form className="inline-form" onSubmit={(event) => void reset(event)}><label>Owner email<input name="recovery-email" type="email" autoComplete="email" required /></label><button className="button" type="submit" disabled={resetBusy}>{resetBusy ? 'Sending…' : 'Send password setup link'}</button></form></details><p role="status" aria-live="polite">{status}</p><small>Codex staging · public signup is disabled</small></section></main>
}

export function OwnerAccessGate({
  children,
  access = configuredOwnerAccess(),
  allowSynthetic = syntheticPreviewEnabled(),
}: {
  children: ReactNode
  access?: OwnerAccessAdapter | null
  allowSynthetic?: boolean
}) {
  const [phase, setPhase] = useState<'checking' | 'authenticated' | 'signed-out' | 'error'>(() => access ? 'checking' : allowSynthetic ? 'authenticated' : 'error')
  useEffect(() => {
    if (!access) return
    let active = true
    void access.getAuthenticated().then((authenticated) => { if (active) setPhase(authenticated ? 'authenticated' : 'signed-out') }).catch(() => { if (active) setPhase('error') })
    const unsubscribe = access.subscribe((authenticated) => { if (active) setPhase(authenticated ? 'authenticated' : 'signed-out') })
    return () => { active = false; unsubscribe() }
  }, [access])

  if (!access && allowSynthetic) return <><aside className="synthetic-preview-banner" role="status">Synthetic preview · no account or live data</aside>{children}</>
  if (phase === 'checking') return <main className="centred-page"><section className="auth-card panel" role="status"><Brand /><h1>Checking owner sign-in…</h1><span className="spinner" aria-hidden="true" /></section></main>
  if (phase === 'signed-out' && access) return <OwnerSignIn access={access} />
  if (phase === 'error') return <main className="centred-page"><section className="auth-card panel" role="alert"><Brand /><h1>Owner workspace unavailable</h1><p>The secured staging connection could not be verified. No synthetic owner data has been loaded.</p><button type="button" className="button" onClick={() => window.location.reload()}>Try again</button></section></main>
  return <>{children}</>
}
